import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

declare const API_BASE_URL: string | undefined;
const ADMIN_TOKEN_KEY = 'nzp_admin_token';
const ADMIN_USER_KEY = 'nzp_admin_user';
const ADMIN_SESSION_MIGRATED_KEY = 'nzp_admin_session_migrated';

export const toastState = signal<{ show: boolean, type: 'success' | 'error', message: string }>({ show: false, type: 'success', message: '' });
export function showToast(type: 'success' | 'error', message: string) {
  toastState.set({ show: true, type, message });
  setTimeout(() => toastState.set({ show: false, type, message }), 5000);
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private http = inject(HttpClient);
  readonly sessionStorageKey = ADMIN_TOKEN_KEY;

  // Update this URL to match your local PHP server host path
  // e.g., 'https://nzplumbinghq.co.nz/api/v1' or 'http://localhost/nzplumbing/api/v1'
  public baseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : 'https://nzplumbinghq.co.nz/api/v1';

  public assetUrl(path: string | null | undefined): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const apiRoot = this.baseUrl.replace(/\/v1\/?$/, '');
    return `${apiRoot}${cleanPath}`;
  }

  // Track state
  currentUser = signal<any>(null);
  pendingEmail = signal<string>(sessionStorage.getItem('nzp_pending_email') || ''); // Used to pass email from login to OTP screen

  constructor() {
    this.migrateLegacySession();
    this.syncSessionFromStorage();
  }

  syncSessionFromStorage() {
    const storedUser = localStorage.getItem(ADMIN_USER_KEY);
    if (storedUser) {
      try { this.currentUser.set(JSON.parse(storedUser)); } catch {}
    } else {
      const token = this.accessToken();
      const tokenUser = token ? this.userFromToken(token) : null;
      if (tokenUser) {
        localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(tokenUser));
        this.currentUser.set(tokenUser);
      } else {
        this.currentUser.set(null);
      }
    }
  }

  private getHeaders() {
    const token = this.accessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }

  // --- AUTH ENDPOINTS --- //

  login(credentials: { email: string; password: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/login.php`, credentials).pipe(
      tap((res: any) => {
        if (res.success) {
          this.pendingEmail.set(credentials.email);
          sessionStorage.setItem('nzp_pending_email', credentials.email);
        }
      })
    );
  }

  verifyOtp(otp: string): Observable<any> {
    const payload = {
      email: this.pendingEmail(),
      otp: otp
    };
    return this.http.post(`${this.baseUrl}/auth/verify_otp.php`, payload).pipe(
      tap((res: any) => {
        if (res.success && res.data?.token) {
          this.storeSession(res.data.token, res.data.user);
          sessionStorage.removeItem('nzp_pending_email');
          this.pendingEmail.set('');
        }
      })
    );
  }

  resendOtp(): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/resend_otp.php`, {
      email: this.pendingEmail()
    });
  }

  logout() {
    localStorage.removeItem(ADMIN_USER_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.setItem(ADMIN_SESSION_MIGRATED_KEY, '1');
    this.currentUser.set(null);
    this.pendingEmail.set('');
    sessionStorage.removeItem('nzp_pending_email');
  }

  isAuthenticated(): boolean {
    const token = this.accessToken();
    if (!token) return false;
    const user = this.userFromToken(token);
    if (!user) {
      this.logout();
      return false;
    }

    try {
      const payload = token.split('.')[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized.padEnd(normalized.length + (4 - normalized.length % 4) % 4, '=')));
      if (decoded?.exp && decoded.exp * 1000 < Date.now()) {
        this.logout();
        return false;
      }
    } catch {
      this.logout();
      return false;
    }

    return true;
  }

  accessToken(): string {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || '';
  }

  refreshSession(): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/refresh.php`, {}, { headers: this.getHeaders() }).pipe(
      tap((res: any) => {
        if (res?.success && res.data?.token) this.storeSession(res.data.token, res.data.user);
      })
    );
  }

  private storeSession(token: string, user: any) {
    localStorage.setItem(ADMIN_SESSION_MIGRATED_KEY, '1');
    if (user) {
      localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
      this.currentUser.set(user);
    }
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
  }

  private migrateLegacySession() {
    if (localStorage.getItem(ADMIN_SESSION_MIGRATED_KEY) === '1') return;
    const legacyToken = localStorage.getItem('nzp_token');
    const legacyUser = localStorage.getItem('nzp_user');
    if (legacyToken) localStorage.setItem(ADMIN_TOKEN_KEY, legacyToken);
    if (legacyUser) localStorage.setItem(ADMIN_USER_KEY, legacyUser);
    localStorage.setItem(ADMIN_SESSION_MIGRATED_KEY, '1');
  }

  // --- JOBS ENDPOINTS --- //

  getJobs(status?: string, includeArchivedWorkflow: boolean = false): Observable<any> {
    let url = `${this.baseUrl}/jobs/list.php`;
    const params: string[] = [];
    if (status) {
      params.push(`status=${encodeURIComponent(status)}`);
    }
    if (includeArchivedWorkflow) params.push('include_archived_workflow=1');
    if (params.length) url += `?${params.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getJob(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/get.php?id=${id}`, { headers: this.getHeaders() });
  }

  updateJobStatus(payload: { id: string; status: string; pause_category?: string; pause_remarks?: string; pause_resolution_notes?: string; office_closeout_note?: string; office_exception_remarks?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/status.php`, payload, { headers: this.getHeaders() });
  }

  updateJobMaterialReadiness(payload: { id: number | string; readiness_status: string; readiness_note?: string; required_by?: string | null }): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/update_material_readiness.php`, payload, { headers: this.getHeaders() });
  }

  scheduleJob(payload: { id: string; scheduled_date: string | null; schedule_change_category?: string; schedule_change_remarks?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/schedule.php`, payload, { headers: this.getHeaders() });
  }

  saveInspectionServiceDecision(payload: { id: string; decision: 'Pending' | 'Service Required' | 'No Service Required'; remarks?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/inspection_service_decision.php`, payload, { headers: this.getHeaders() });
  }

  acceptSafetyAcknowledgement(payload: { reference_id: string; model_type: 'Job' | 'Project'; statement: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/safety/acknowledge.php`, payload, { headers: this.getHeaders() });
  }

  sendSafetyDeclineEmail(acknowledgementId: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/safety/send_decline_email.php`, { acknowledgement_id: acknowledgementId }, { headers: this.getHeaders() });
  }

  resolveSafetyConcern(payload: { acknowledgement_id: string | number; resolution_notes?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/safety/resolve.php`, payload, { headers: this.getHeaders() });
  }

  createJob(jobData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/create.php`, jobData, { headers: this.getHeaders() });
  }

  assignWorker(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/assign.php`, payload, { headers: this.getHeaders() });
  }

  archiveReference(payload: { reference_id: string | number; model_type: 'Job' | 'Project'; remarks: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/archive/save.php`, payload, { headers: this.getHeaders() });
  }

  getArchivedJobs(): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/list.php?archived=1`, { headers: this.getHeaders() });
  }

  restoreReference(payload: { reference_id: string | number; model_type: 'Job' | 'Project'; remarks: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/archive/restore.php`, payload, { headers: this.getHeaders() });
  }

  // --- COMPLIANCE --- //
  getAudits(): Observable<any> {
    return this.http.get(`${this.baseUrl}/audits/list.php`, { headers: this.getHeaders() });
  }

  saveAudit(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/audits/save.php`, payload, { headers: this.getHeaders() });
  }

  downloadAuditPdf(auditId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/audits/pdf.php?id=${encodeURIComponent(auditId)}`, {
      headers: this.getHeaders(),
      responseType: 'blob'
    });
  }

  createProject(projectData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/create.php`, projectData, { headers: this.getHeaders() });
  }

  // --- PROJECTS ENDPOINTS --- //

  getProjects(status?: string): Observable<any> {
    let url = `${this.baseUrl}/projects/list.php`;
    if (status) {
      url += `?status=${status}`;
    }
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getProject(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/projects/get.php?id=${id}`, { headers: this.getHeaders() });
  }

  updateProjectPhase(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/phases.php`, payload, { headers: this.getHeaders() });
  }

  saveProjectPhaseForm(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/save_phase_form.php`, payload, { headers: this.getHeaders() });
  }

  saveProjectCertificate(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/save_certificate.php`, payload, { headers: this.getHeaders() });
  }

  getComplianceForms(certType: 'PS3_Plumbing' | 'PS3_Drainage' | 'Gas_Safety'): Observable<any> {
    return this.http.get(`${this.baseUrl}/forms/list.php?cert_type=${encodeURIComponent(certType)}`, { headers: this.getHeaders() });
  }

  saveComplianceForm(payload: {
    id?: number;
    cert_type: 'PS3_Plumbing' | 'PS3_Drainage' | 'Gas_Safety';
    form_name: string;
    project_id?: string | null;
    form_data: any;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/forms/save.php`, payload, { headers: this.getHeaders() });
  }

  downloadComplianceFormPdf(formId: number, certType: 'PS3_Plumbing' | 'PS3_Drainage' | 'Gas_Safety'): Observable<Blob> {
    const scripts = {
      PS3_Plumbing: 'generate_ps3_plumbing.php',
      PS3_Drainage: 'generate_ps3_drainage.php',
      Gas_Safety: 'generate_gas_certificate.php'
    } as const;
    const script = scripts[certType];
    return this.http.get(`${this.baseUrl}/projects/${script}?form_id=${encodeURIComponent(String(formId))}`, {
      headers: this.getHeaders(),
      responseType: 'blob'
    });
  }

  updateProject(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/update.php`, payload, { headers: this.getHeaders() });
  }

  saveProjectWorkAssignment(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/work_assignments.php`, payload, { headers: this.getHeaders() });
  }

  deleteProjectWorkAssignment(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/work_assignments.php`, { action: 'delete', id }, { headers: this.getHeaders() });
  }

  getWorkSessions(projectId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/projects/work_session.php?project_id=${projectId}`, { headers: this.getHeaders() });
  }

  getProjectDeclaration(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/projects/declaration_get.php?id=${encodeURIComponent(id)}`, { headers: this.getHeaders() });
  }

  saveProjectDeclaration(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/declaration_save.php`, payload);
  }

  sendDeclarationEmail(projectId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/declaration_send_email.php`, { project_id: projectId }, { headers: this.getHeaders() });
  }

  skipProjectDeclaration(projectId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/declaration_skip.php`, { project_id: projectId }, { headers: this.getHeaders() });
  }

  uploadProjectDocument(formData: FormData): Observable<any> {
    const token = this.accessToken();
    const headers = new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });
    return this.http.post(`${this.baseUrl}/projects/documents.php`, formData, { headers });
  }

  // --- USERS / WORKERS / CUSTOMERS --- //

  getWorkers(): Observable<any> {
    return this.http.get(`${this.baseUrl}/users/list.php?role=Worker`, { headers: this.getHeaders() });
  }

  getStaff(): Observable<any> {
    return this.http.get(`${this.baseUrl}/users/list.php`, { headers: this.getHeaders() });
  }

  createStaff(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/users/create.php`, payload, { headers: this.getHeaders() });
  }

  updateStaff(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/users/update.php`, payload, { headers: this.getHeaders() });
  }

  updateStaffPhoto(userId: number, photo: File | null, remove = false): Observable<any> {
    const formData = new FormData();
    formData.append('user_id', String(userId));
    if (remove) formData.append('remove', '1');
    if (photo) formData.append('photo', photo);
    const token = this.accessToken();
    const headers = new HttpHeaders({ 'Authorization': token ? `Bearer ${token}` : '' });
    return this.http.post(`${this.baseUrl}/users/photo.php`, formData, { headers });
  }

  saveStaffVisa(formData: FormData): Observable<any> {
    const token = this.accessToken();
    const headers = new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });
    return this.http.post(`${this.baseUrl}/users/visa_save.php`, formData, { headers });
  }

  deleteStaffVisa(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/users/visa_delete.php`, { id }, { headers: this.getHeaders() });
  }

  getDocumentLibrary(): Observable<any> {
    return this.http.get(`${this.baseUrl}/documents/list.php`, { headers: this.getHeaders() });
  }

  saveDocumentFolder(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/documents/folder_save.php`, payload, { headers: this.getHeaders() });
  }

  deleteDocumentFolder(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/documents/folder_delete.php`, { id }, { headers: this.getHeaders() });
  }

  uploadLibraryDocument(formData: FormData): Observable<any> {
    const token = this.accessToken();
    const headers = new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });
    return this.http.post(`${this.baseUrl}/documents/upload.php`, formData, { headers });
  }

  deleteLibraryDocument(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/documents/delete.php`, { id }, { headers: this.getHeaders() });
  }

  resendActivation(userId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/users/resend_activation.php`, { user_id: userId }, { headers: this.getHeaders() });
  }

  convertStaffToInvite(userId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/users/convert_to_invite.php`, { user_id: userId }, { headers: this.getHeaders() });
  }

  getFleetVehicles(): Observable<any> {
    return this.http.get(`${this.baseUrl}/fleet/list.php`, { headers: this.getHeaders() });
  }

  createFleetVehicle(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/fleet/create.php`, payload, { headers: this.getHeaders() });
  }

  updateFleetVehicle(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/fleet/update.php`, payload, { headers: this.getHeaders() });
  }

  updateFleetOdometer(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/fleet/update_odometer.php`, payload, { headers: this.getHeaders() });
  }

  assignFleetVehicle(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/fleet/assign.php`, payload, { headers: this.getHeaders() });
  }

  deleteFleetVehicle(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/fleet/delete.php`, { id }, { headers: this.getHeaders() });
  }

  getActiveLocations(): Observable<any> {
    return this.http.get(`${this.baseUrl}/locations/active.php`, { headers: this.getHeaders() });
  }

  getWorkerMapLocations(): Observable<any> {
    return this.http.get(`${this.baseUrl}/locations/workers_map.php`, { headers: this.getHeaders() });
  }

  searchCustomerByPhone(phone: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/customers/search.php?phone=${encodeURIComponent(phone)}`, { headers: this.getHeaders() });
  }

  getCustomers(): Observable<any> {
    return this.http.get(`${this.baseUrl}/customers/list.php`, { headers: this.getHeaders() });
  }

  getCustomer(id: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/customers/get.php?id=${id}`, { headers: this.getHeaders() });
  }

  createCustomer(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/customers/create.php`, payload, { headers: this.getHeaders() });
  }

  updateCustomer(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/customers/update.php`, payload, { headers: this.getHeaders() });
  }

  deleteCustomer(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/customers/delete.php`, { id }, { headers: this.getHeaders() });
  }

  // --- PASSWORD ADMINISTRATION ENDPOINTS --- //

  verifyActivateToken(token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/auth/activate_verify.php?token=${token}`);
  }

  submitActivate(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/activate_submit.php`, payload);
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/forgot_password.php`, { email });
  }

  submitResetPassword(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/reset_password.php`, payload);
  }

  getProfile(): Observable<any> {
    return this.http.get(`${this.baseUrl}/auth/profile.php`, { headers: this.getHeaders() }).pipe(
      tap((res: any) => {
        const profile = res?.data?.profile;
        if (res.success && profile) {
          const user = {
            id: profile.id,
            name: profile.name,
            email: profile.email,
            role: profile.role,
            can_work_as_worker: Number(profile.can_work_as_worker || 0),
            has_worker_access: !!profile.has_worker_access
          };
          localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
          this.currentUser.set(user);
        }
      })
    );
  }

  updateProfile(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/profile.php`, payload, { headers: this.getHeaders() });
  }

  changePassword(payload: { current_password: string; new_password: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/change_password.php`, payload, { headers: this.getHeaders() });
  }

  // --- SETTINGS PORTAL --- //

  getCompanySettings(): Observable<any> {
    return this.http.get(`${this.baseUrl}/settings/get.php`, { headers: this.getHeaders() });
  }

  updateCompanySettings(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/settings/update.php`, data, { headers: this.getHeaders() });
  }

  // --- QUOTES ENGINE --- //

  getQuotes(params: { status?: string, search?: string } = {}): Observable<any> {
    let url = `${this.baseUrl}/quotes/list.php`;
    const queryParams: string[] = [];
    if (params.status) queryParams.push(`status=${encodeURIComponent(params.status)}`);
    if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getQuoteContext(referenceId: string, modelType: string = 'Job'): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/get_quote_context.php?job_id=${encodeURIComponent(referenceId)}&model_type=${encodeURIComponent(modelType)}`, { headers: this.getHeaders() });
  }

  getQuote(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/quotes/get.php?id=${id}`, { headers: this.getHeaders() });
  }

  createQuote(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotes/create.php`, payload, { headers: this.getHeaders() });
  }

  updateQuote(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotes/update.php`, payload, { headers: this.getHeaders() });
  }

  deleteQuote(id: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotes/delete.php`, { id }, { headers: this.getHeaders() });
  }

  generateQuotePdf(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotes/generate_pdf.php`, payload, { headers: this.getHeaders() });
  }

  sendQuoteToCustomer(quoteId: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotes/send.php`, { quote_id: quoteId }, { headers: this.getHeaders() });
  }

  getPublicQuote(token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/quotes/public_get.php?token=${encodeURIComponent(token)}`);
  }

  respondToPublicQuote(payload: { token: string; action: 'accept' | 'decline'; signature_data?: string; reason?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotes/public_respond.php`, payload);
  }

  // --- CUSTOMER FEEDBACK --- //

  sendFeedbackRequest(payload: { reference_id: string; model_type: 'Job' | 'Project'; customer_email?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/customer_feedback/send.php`, payload, { headers: this.getHeaders() });
  }

  getCustomerFeedback(params: any = {}): Observable<any> {
    let url = `${this.baseUrl}/customer_feedback/list.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  updateCustomerFeedbackStatus(payload: { id: number; status: string; follow_up_notes?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/customer_feedback/update_status.php`, payload, { headers: this.getHeaders() });
  }

  getPublicFeedback(token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/customer_feedback/public_get.php?token=${encodeURIComponent(token)}`);
  }

  submitPublicFeedback(payload: { token: string; rating: number; comments?: string; service_category?: string; testimonial_permission?: boolean }): Observable<any> {
    return this.http.post(`${this.baseUrl}/customer_feedback/public_submit.php`, payload);
  }

  // --- IN-APP NOTIFICATIONS --- //

  getNotifications(params: { limit?: number; unread_only?: boolean; status?: string; priority?: string; type?: string; reference_type?: string } = {}): Observable<any> {
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value === true ? 1 : value))}`);
      }
    });
    const query = queryParams.length ? `?${queryParams.join('&')}` : '';
    return this.http.get(`${this.baseUrl}/notifications/list.php${query}`, { headers: this.getHeaders() });
  }

  globalSearch(query: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/search/global.php?q=${encodeURIComponent(query)}`, { headers: this.getHeaders() });
  }

  markNotificationRead(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/notifications/mark_read.php`, { id }, { headers: this.getHeaders() });
  }

  markAllNotificationsRead(): Observable<any> {
    return this.http.post(`${this.baseUrl}/notifications/mark_read.php`, { all: true }, { headers: this.getHeaders() });
  }

  // --- PURCHASE ORDERS --- //

  getPurchaseOrders(params: any = {}): Observable<any> {
    let url = `${this.baseUrl}/purchase_orders/list.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getPurchaseOrder(id: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}/purchase_orders/get.php?id=${encodeURIComponent(String(id))}`, { headers: this.getHeaders() });
  }

  savePurchaseOrder(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/save.php`, payload, { headers: this.getHeaders() });
  }

  sendPurchaseOrder(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/send.php`, { purchase_order_id: id }, { headers: this.getHeaders() });
  }

  updatePurchaseOrderStock(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/update_stock.php`, { purchase_order_id: id }, { headers: this.getHeaders() });
  }

  getPurchaseOrderReports(params: any = {}): Observable<any> {
    let url = `${this.baseUrl}/purchase_orders/reports.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getSuppliers(search: string = ''): Observable<any> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.http.get(`${this.baseUrl}/purchase_orders/suppliers.php${query}`, { headers: this.getHeaders() });
  }

  saveSupplier(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/supplier_save.php`, payload, { headers: this.getHeaders() });
  }

  deleteSupplier(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/supplier_delete.php`, { id }, { headers: this.getHeaders() });
  }

  getPurchaseOrderTemplates(includeItems: boolean = false): Observable<any> {
    const query = includeItems ? '?include_items=1' : '';
    return this.http.get(`${this.baseUrl}/purchase_orders/templates.php${query}`, { headers: this.getHeaders() });
  }

  getPurchaseOrderTemplate(id: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}/purchase_orders/template_get.php?id=${encodeURIComponent(String(id))}`, { headers: this.getHeaders() });
  }

  savePurchaseOrderTemplate(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/template_save.php`, payload, { headers: this.getHeaders() });
  }

  deletePurchaseOrderTemplate(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/template_delete.php`, { id }, { headers: this.getHeaders() });
  }

  // --- LEAVE REQUESTS --- //

  getLeaveRequests(params: any = {}): Observable<any> {
    let url = `${this.baseUrl}/leave_requests/list.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  decideLeaveRequest(payload: { id: string | number; decision: 'Approved' | 'Declined'; admin_notes?: string; attach_pdf?: boolean }): Observable<any> {
    return this.http.post(`${this.baseUrl}/leave_requests/decision.php`, payload, { headers: this.getHeaders() });
  }

  getTimesheets(params: { from?: string; to?: string; staff_id?: string | number } = {}): Observable<any> {
    let url = `${this.baseUrl}/timesheets/list.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  updateWeeklyShifts(payload: {
    staff_id: number;
    week_start: string;
    week_end: string;
    reason?: string;
    shifts: Array<{ id: number | null; started_at: string; ended_at: string }>;
    deleted_shift_ids?: number[];
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/timesheets/update_week.php`, payload, { headers: this.getHeaders() });
  }

  getStaffCalendar(params: { from?: string; to?: string; staff_id?: string | number } = {}): Observable<any> {
    let url = `${this.baseUrl}/operations/staff_calendar.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getAdminOperationsReport(params: { period?: string; from?: string; to?: string } = {}): Observable<any> {
    let url = `${this.baseUrl}/reports/admin_operations.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getPublicLeaveReview(token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/leave_requests/public_review.php?token=${encodeURIComponent(token)}`);
  }

  submitPublicLeaveReview(payload: { token: string; decision: 'Approved' | 'Declined'; admin_notes?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/leave_requests/public_review.php`, payload);
  }

  // --- WORK ORDERS --- //

  getWorkOrders(params: any = {}): Observable<any> {
    let url = `${this.baseUrl}/work_orders/list.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getWorkOrder(id: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}/work_orders/get.php?id=${encodeURIComponent(String(id))}`, { headers: this.getHeaders() });
  }

  saveWorkOrder(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/work_orders/save.php`, payload, { headers: this.getHeaders() });
  }

  sendWorkOrder(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/work_orders/send.php`, { work_order_id: id }, { headers: this.getHeaders() });
  }

  sendInsuranceReport(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/ai/send_insurance_report.php`, { report_id: id }, { headers: this.getHeaders() });
  }

  // --- INVOICES ENGINE --- //

  getInvoices(params: { status?: string, search?: string } = {}): Observable<any> {
    let url = `${this.baseUrl}/invoices/list.php`;
    const queryParams: string[] = [];
    if (params.status) queryParams.push(`status=${encodeURIComponent(params.status)}`);
    if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getInvoice(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/invoices/get.php?id=${encodeURIComponent(id)}`, { headers: this.getHeaders() });
  }

  saveInvoice(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/invoices/save.php`, payload, { headers: this.getHeaders() });
  }

  recordInvoicePayment(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/invoices/add_payment.php`, payload, { headers: this.getHeaders() });
  }

  recordInvoiceAdjustment(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/invoices/add_adjustment.php`, payload, { headers: this.getHeaders() });
  }

  createInvoiceFromQuote(quoteId: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/invoices/from_quote.php`, { quote_id: quoteId }, { headers: this.getHeaders() });
  }

  sendInvoiceToCustomer(invoiceId: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/invoices/send.php`, { invoice_id: invoiceId }, { headers: this.getHeaders() });
  }

  getPublicInvoice(token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/invoices/public_get.php?token=${encodeURIComponent(token)}`);
  }

  generateInvoicePdf(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/invoices/generate_pdf.php`, payload, { headers: this.getHeaders() });
  }

  // --- PHOTOS & NOTES --- //

  getPhotos(jobId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/photos.php?reference_id=${jobId}&model_type=Job`, { headers: this.getHeaders() });
  }

  getProjectPhotos(projectId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/photos.php?reference_id=${encodeURIComponent(projectId)}&model_type=Project`, { headers: this.getHeaders() });
  }

  downloadPhoto(photoId: number | string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/jobs/photo_download.php?id=${encodeURIComponent(photoId)}`, {
      headers: this.getHeaders(), responseType: 'blob'
    });
  }

  getNotes(jobId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/notes.php?reference_id=${jobId}&model_type=Job`, { headers: this.getHeaders() });
  }

  // --- MATERIALS --- //

  getMaterials(params: { search?: string, parent_id?: number, limit?: number, page?: number, include_children?: boolean } = {}): Observable<any> {
    let url = `${this.baseUrl}/materials/list.php`;
    const queryParams: string[] = [];
    if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
    if (params.parent_id !== undefined) queryParams.push(`parent_id=${params.parent_id}`);
    if (params.limit !== undefined) queryParams.push(`limit=${params.limit}`);
    if (params.page !== undefined) queryParams.push(`page=${params.page}`);
    if (params.include_children !== undefined) queryParams.push(`include_children=${params.include_children ? '1' : '0'}`);
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getMaterial(id: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/materials/get.php?id=${id}`, { headers: this.getHeaders() });
  }

  createMaterial(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/materials/create.php`, payload, { headers: this.getHeaders() });
  }

  updateMaterial(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/materials/update.php`, payload, { headers: this.getHeaders() });
  }

  deleteMaterial(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/materials/delete.php`, { id }, { headers: this.getHeaders() });
  }

  private userFromToken(token: string): any | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized.padEnd(normalized.length + (4 - normalized.length % 4) % 4, '=')));
      if (!decoded?.sub) return null;
      return {
        id: decoded.sub,
        name: decoded.name || '',
        email: decoded.email || '',
        role: decoded.role || '',
        can_work_as_worker: Number(decoded.can_work_as_worker || 0),
        has_worker_access: !!decoded.has_worker_access
      };
    } catch {
      return null;
    }
  }
}
